import { ApiError } from '../../identity/types';
import { getMockCurrentUser } from '../../identity/services/mockCurrentUser';
import { clearMockDelegation, getMockDelegation, setMockDelegation } from '../../identity/services/mockDelegationState';
import type {
  AuthorizedPartner,
  EnterClientSummary,
  InvitationPreview,
  InviteClientRequest,
  PartnerAssignment,
  PartnerClient,
  PartnerClientInvitationResult,
  PartnerHistoryEntry,
  PartnerMeResponse,
  PartnerOrganization,
  PartnerRole,
  PartnerStatus,
  PartnerTeamMember,
  RelationshipStatus,
  ServiceOption,
} from '../types';
import { PARTNER_SERVICES } from '../types';
import type { CompanyPartnersGateway, PartnerGateway } from './PartnerGateway';

/**
 * MOCK MODE ONLY: Synthetic Demo Data. Nothing here is real, and nothing here is production data. One in-memory firm with deterministic people and five client companies at different
 * stages, so the Partner Portal can be developed and reviewed without a backend. The real rules are enforced by the API, not here: this mock only mirrors the shapes and the most visible
 * behaviors (an invitation secret is shown once, a revoked relationship stays revoked, a read-only person cannot invite).
 */
export const MOCK_PARTNER_EMAIL = 'partner@epn.local';
export const MOCK_PARTNER_ORG_ID = 'aaaaaaaa-aaaa-aaaa-aaaa-aaaaaaaaaa01';
export const SYNTHETIC_FIRM_NAME = 'Escochex Partner Accounting Group';
export const SYNTHETIC_AGREEMENT_VERSION = '2026-11-demo';

const ROLE_PERMISSIONS: Record<PartnerRole, string[]> = {
  Owner: ['partner.agreement.accept', 'partner.assignments.manage', 'partner.clients.manage', 'partner.clients.read', 'partner.clients.work', 'partner.org.manage', 'partner.org.read', 'partner.reports.read', 'partner.revenue.read', 'partner.team.manage', 'partner.team.read'],
  Admin: ['partner.assignments.manage', 'partner.clients.manage', 'partner.clients.read', 'partner.clients.work', 'partner.employees.manage', 'partner.org.read', 'partner.reports.read', 'partner.team.manage', 'partner.team.read'],
  PayrollManager: ['partner.assignments.manage', 'partner.clients.read', 'partner.clients.work', 'partner.employees.manage', 'partner.org.read', 'partner.payroll.review', 'partner.reports.read', 'partner.team.read'],
  PayrollProcessor: ['partner.clients.read', 'partner.clients.work', 'partner.employees.manage', 'partner.org.read', 'partner.payroll.prepare', 'partner.reports.read'],
  ReadOnly: ['partner.clients.read', 'partner.org.read', 'partner.reports.read'],
};

const iso = (daysFromNow: number) => new Date(Date.UTC(2026, 10, 1) + daysFromNow * 86_400_000).toISOString();

interface MockState {
  status: PartnerStatus;
  callerRole: PartnerRole;
  team: PartnerTeamMember[];
  clients: PartnerClient[];
  assignments: Map<string, PartnerAssignment[]>;
  history: Map<string, PartnerHistoryEntry[]>;
  tokens: Map<string, string>; // token -> relationship id
}

const member = (n: number, email: string, name: string, role: PartnerRole): PartnerTeamMember => ({
  membershipId: `bbbbbbbb-bbbb-bbbb-bbbb-bbbbbbbbbb0${n}`, userId: `cccccccc-cccc-cccc-cccc-cccccccccc0${n}`, email, displayName: name, role, isActive: true, createdAtUtc: iso(-120),
});

const client = (n: number, name: string, status: RelationshipStatus, granted: string[], extra: Partial<PartnerClient> = {}): PartnerClient => ({
  id: `dddddddd-dddd-dddd-dddd-dddddddddd0${n}`, clientName: name, clientCompanyName: status === 'InvitationSent' ? null : name, invitedEmail: `owner@${name.toLowerCase().replace(/[^a-z]+/g, '')}.example`,
  status, requestedServices: granted, grantedServices: status === 'InvitationSent' ? [] : granted, accessExpiresAtUtc: null, invitedAtUtc: iso(-90 + n), invitationExpiresAtUtc: null,
  acceptedAtUtc: status === 'InvitationSent' ? null : iso(-85 + n), activatedAtUtc: status === 'Active' ? iso(-80 + n) : null, revokedAtUtc: null, endedAtUtc: null, lastReviewedAtUtc: iso(-30), assignedMemberCount: 0, ...extra,
});

function freshState(): MockState {
  const team = [
    member(1, MOCK_PARTNER_EMAIL, 'Pat Partner', 'Owner'),
    member(2, 'admin.partner@escochex-demo.example', 'Alex Admin', 'Admin'),
    member(3, 'processor1.partner@escochex-demo.example', 'Sam Processor', 'PayrollProcessor'),
    member(4, 'processor2.partner@escochex-demo.example', 'Riley Processor', 'PayrollProcessor'),
    member(5, 'readonly.partner@escochex-demo.example', 'Jordan ReadOnly', 'ReadOnly'),
  ];
  const full = ['company.setup', 'employees.setup', 'payroll.prepare', 'payroll.review', 'reports.payroll'];
  const clients = [
    client(1, 'Lone Star Roofing (weekly)', 'Active', full, { accessExpiresAtUtc: iso(240) }),
    client(2, 'Greenway Dental (biweekly)', 'Active', full),
    client(3, 'Harbor Brewing (semi-monthly)', 'Active', full.slice(0, 4), { accessExpiresAtUtc: iso(21) }),
    client(4, 'Summit Fitness (onboarding)', 'Onboarding', ['company.setup', 'employees.setup', 'history.import']),
    client(5, 'Old Mill Bakery (access revoked)', 'Revoked', full.slice(0, 3), { revokedAtUtc: iso(-14) }),
  ];
  const assignments = new Map<string, PartnerAssignment[]>();
  const owner = team[0]!;
  const sam = team[2]!;
  for (const c of clients.slice(0, 4)) {
    const rows: PartnerAssignment[] = [{ assignmentId: `eeeeeeee-eeee-eeee-eeee-eeeeeeeeee${c.id.slice(-2)}`, membershipId: sam.membershipId, email: sam.email, displayName: sam.displayName, role: sam.role, assignedAtUtc: iso(-60) }];
    assignments.set(c.id, rows);
    c.assignedMemberCount = rows.length;
  }
  void owner;
  return { status: 'Active', callerRole: 'Owner', team, clients, assignments, history: new Map(), tokens: new Map() };
}

let state = freshState();

/** The demo company this mock signed-in owner belongs to: two of the five seeded relationships (one active, one revoked) are THIS company's; the other three belong to other clients of the firm.
 * A relationship the owner accepts during the session joins them. */
const SEEDED_COMPANY_RELATIONSHIPS = ['dddddddd-dddd-dddd-dddd-dddddddddd01', 'dddddddd-dddd-dddd-dddd-dddddddddd05'];
let companyRelationshipIds = new Set<string>(SEEDED_COMPANY_RELATIONSHIPS);

/** TEST-ONLY: back to the deterministic seed, with the caller as the given role and the firm in the given status. */
export function resetMockPartnerStateForTests(options: { role?: PartnerRole; status?: PartnerStatus } = {}): void {
  state = freshState();
  clearMockDelegation();
  companyRelationshipIds = new Set<string>(SEEDED_COMPANY_RELATIONSHIPS);
  state.callerRole = options.role ?? 'Owner';
  state.status = options.status ?? 'Active';
}

/** The mock identity that is the partner user. Every other mock identity has no partner membership, which is exactly how the real server treats an ordinary user. */
function callerIsPartner(): boolean {
  return getMockCurrentUser()?.email === MOCK_PARTNER_EMAIL;
}

function requirePartner(organizationId: string): void {
  if (!callerIsPartner() || organizationId !== MOCK_PARTNER_ORG_ID) {
    throw new ApiError(403, 'Permission denied.');
  }
}

function can(permission: string): void {
  if (!ROLE_PERMISSIONS[state.callerRole].includes(permission)) {
    throw new ApiError(403, 'Permission denied.');
  }
}

function record(relationshipId: string, eventType: string, reason: string | null = null) {
  const rows = state.history.get(relationshipId) ?? [];
  rows.push({ occurredAtUtc: new Date().toISOString(), eventType, actorEmail: MOCK_PARTNER_EMAIL, outcome: 'Succeeded', reason, detailsJson: null });
  state.history.set(relationshipId, rows);
}

function newToken(relationshipId: string): string {
  const token = `demo-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  for (const [existing, id] of state.tokens) {
    if (id === relationshipId) state.tokens.delete(existing);
  }
  state.tokens.set(token, relationshipId);
  return token;
}

// ---- entering a client (mock): mirrors what the real server decides, from the same inputs. The real token is an httpOnly cookie; the mock keeps only the display facts. ----
const MOCK_SERVICE_SCOPES: Record<string, string[]> = {
  'company.setup': ['company.setup', 'tax.setup.prepare'], 'employees.setup': ['employees.setup'], 'history.import': ['history.import'], 'benefits.setup': ['benefits.setup'],
  'accounting.export': ['accounting.export'], 'reports.payroll': ['payroll.read'], 'payroll.review': ['payroll.read'], 'payroll.prepare': ['payroll.read', 'payroll.prepare'],
  'tax.payments.support': ['tax.read'], 'tax.filings.support': ['tax.read', 'tax.filing.prepare'], other: [],
};
/** The partner role permission a person needs for a service. Tax support and Other have none yet (the server denies them until the business decides who may use them). */
const MOCK_SERVICE_PERMISSION: Record<string, string | null> = {
  'company.setup': 'partner.employees.manage', 'employees.setup': 'partner.employees.manage', 'history.import': 'partner.employees.manage', 'benefits.setup': 'partner.employees.manage',
  'accounting.export': 'partner.employees.manage', 'reports.payroll': 'partner.reports.read', 'payroll.review': 'partner.payroll.review', 'payroll.prepare': 'partner.payroll.prepare',
};
const MOCK_SETUP_SERVICES = new Set(['company.setup', 'employees.setup', 'history.import', 'benefits.setup']);
const MOCK_SCOPE_PERMISSIONS: Record<string, string[]> = { 'payroll.read': ['payrollruns.read', 'reports.read'], 'payroll.prepare': ['payrollruns.write'] };

/** When a client ends, suspends or revokes the relationship, a session working inside it stops being valid at once (the real server answers from the database on every request). */
export function invalidateMockDelegationFor(relationshipId: string): void {
  const current = getMockDelegation();
  if (current && current.info.relationshipId === relationshipId) {
    setMockDelegation({ info: { ...current.info, isStillValid: false }, permissions: current.permissions });
  }
}

const clientById = (id: string) => {
  const found = state.clients.find((c) => c.id === id);
  if (!found) throw new ApiError(404, 'Not found.');
  return found;
};

export class MockPartnerGateway implements PartnerGateway {
  async getMe(): Promise<PartnerMeResponse> {
    if (!callerIsPartner()) return { memberships: [], mfaCompleted: false };
    return {
      memberships: [{ partnerOrganizationId: MOCK_PARTNER_ORG_ID, legalName: SYNTHETIC_FIRM_NAME, status: state.status, role: state.callerRole, permissions: ROLE_PERMISSIONS[state.callerRole] }],
      mfaCompleted: true,
    };
  }

  async getOrganization(organizationId: string): Promise<PartnerOrganization> {
    requirePartner(organizationId);
    can('partner.org.read');
    return {
      id: MOCK_PARTNER_ORG_ID, legalName: SYNTHETIC_FIRM_NAME, dba: null, firmType: 'CpaFirm', einLast4: '0000', primaryContactName: 'Pat Partner', primaryContactEmail: MOCK_PARTNER_EMAIL,
      primaryContactPhone: '555-0100', supportEmail: 'support@escochex-demo.example', website: 'https://demo.example', statesServed: ['OK', 'TX'], verificationStatus: 'Verified', status: state.status,
      statusReason: state.status === 'InformationRequired' ? 'Please upload proof of your state licence.' : null, agreementVersion: SYNTHETIC_AGREEMENT_VERSION,
      agreementAcceptedAtUtc: state.status === 'Active' ? iso(-100) : null, createdAtUtc: iso(-120), approvedAtUtc: iso(-110), activatedAtUtc: state.status === 'Active' ? iso(-100) : null,
    };
  }

  async acceptAgreement(organizationId: string, version: string): Promise<PartnerOrganization> {
    requirePartner(organizationId);
    can('partner.agreement.accept');
    if (state.status !== 'Approved') throw new ApiError(403, 'This partner firm is not currently able to do this.');
    if (version !== SYNTHETIC_AGREEMENT_VERSION) throw new ApiError(400, 'The agreement version does not match the one offered.');
    state.status = 'Active';
    return this.getOrganization(organizationId);
  }

  async listClients(organizationId: string): Promise<PartnerClient[]> {
    requirePartner(organizationId);
    can('partner.clients.read');
    const all = state.callerRole === 'Owner' || state.callerRole === 'Admin';
    const mine = state.team.find((m) => m.role === state.callerRole)?.membershipId;
    return state.clients.filter((c) => all || (state.assignments.get(c.id) ?? []).some((a) => a.membershipId === mine)).map((c) => ({ ...c }));
  }

  async getClient(organizationId: string, relationshipId: string): Promise<PartnerClient> {
    const found = (await this.listClients(organizationId)).find((c) => c.id === relationshipId);
    if (!found) throw new ApiError(404, 'Not found.');
    return found;
  }

  async inviteClient(organizationId: string, request: InviteClientRequest): Promise<PartnerClientInvitationResult> {
    requirePartner(organizationId);
    can('partner.clients.manage');
    if (state.status !== 'Active') throw new ApiError(403, 'This partner firm is not currently able to do this.');
    if (!request.clientName.trim()) throw new ApiError(400, 'Client name is required.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(request.invitedEmail)) throw new ApiError(400, 'Client contact email is not a valid email address.');
    if (request.services.length === 0) throw new ApiError(400, 'Choose at least one service.');
    const created: PartnerClient = {
      id: `dddddddd-dddd-dddd-dddd-ddddddddd${String(state.clients.length + 1).padStart(3, '0')}`, clientName: request.clientName.trim(), clientCompanyName: null, invitedEmail: request.invitedEmail.trim(),
      status: 'InvitationSent', requestedServices: [...request.services].sort(), grantedServices: [], accessExpiresAtUtc: null, invitedAtUtc: new Date().toISOString(),
      invitationExpiresAtUtc: new Date(Date.now() + 14 * 86_400_000).toISOString(), acceptedAtUtc: null, activatedAtUtc: null, revokedAtUtc: null, endedAtUtc: null, lastReviewedAtUtc: null, assignedMemberCount: 0,
    };
    state.clients.unshift(created);
    record(created.id, 'PartnerClientInvited');
    return { client: { ...created }, invitationToken: newToken(created.id) };
  }

  async resendInvitation(organizationId: string, relationshipId: string): Promise<PartnerClientInvitationResult> {
    requirePartner(organizationId);
    can('partner.clients.manage');
    const c = clientById(relationshipId);
    if (c.status !== 'InvitationSent' && c.status !== 'AwaitingClientAcceptance') throw new ApiError(409, 'Only an invitation that has not been answered can be re-sent.');
    record(c.id, 'PartnerClientInvitationResent');
    return { client: { ...c }, invitationToken: newToken(c.id) };
  }

  async endRelationship(organizationId: string, relationshipId: string, reason: string): Promise<PartnerClient> {
    requirePartner(organizationId);
    can('partner.clients.manage');
    const c = clientById(relationshipId);
    if (c.status === 'Revoked' || c.status === 'Ended') throw new ApiError(409, 'This relationship has already ended or been revoked.');
    if (reason.trim().length < 10) throw new ApiError(400, 'A reason of at least 10 characters is required.');
    c.status = 'Ended';
    c.endedAtUtc = new Date().toISOString();
    state.assignments.set(c.id, []);
    c.assignedMemberCount = 0;
    invalidateMockDelegationFor(c.id);
    record(c.id, 'PartnerClientRelationshipEnded', reason);
    return { ...c };
  }

  async listAssignments(organizationId: string, relationshipId: string): Promise<PartnerAssignment[]> {
    await this.getClient(organizationId, relationshipId);
    return [...(state.assignments.get(relationshipId) ?? [])];
  }

  async assign(organizationId: string, relationshipId: string, membershipId: string): Promise<PartnerAssignment> {
    requirePartner(organizationId);
    can('partner.assignments.manage');
    const c = clientById(relationshipId);
    if (c.status === 'Revoked' || c.status === 'Ended') throw new ApiError(409, 'Nobody can be assigned to a relationship that has ended or been revoked.');
    const person = state.team.find((m) => m.membershipId === membershipId && m.isActive);
    if (!person) throw new ApiError(404, 'Not found.');
    const rows = state.assignments.get(relationshipId) ?? [];
    const existing = rows.find((r) => r.membershipId === membershipId);
    if (existing) return existing;
    const row: PartnerAssignment = { assignmentId: `eeeeeeee-${Math.random().toString(16).slice(2, 6)}-eeee-eeee-eeeeeeeeeeee`, membershipId, email: person.email, displayName: person.displayName, role: person.role, assignedAtUtc: new Date().toISOString() };
    rows.push(row);
    state.assignments.set(relationshipId, rows);
    c.assignedMemberCount = rows.length;
    record(c.id, 'PartnerClientAssignmentAdded');
    return row;
  }

  async unassign(organizationId: string, relationshipId: string, assignmentId: string): Promise<void> {
    requirePartner(organizationId);
    can('partner.assignments.manage');
    const c = clientById(relationshipId);
    const rows = (state.assignments.get(relationshipId) ?? []).filter((r) => r.assignmentId !== assignmentId);
    state.assignments.set(relationshipId, rows);
    c.assignedMemberCount = rows.length;
    record(c.id, 'PartnerClientAssignmentRemoved');
  }

  async clientHistory(organizationId: string, relationshipId: string): Promise<PartnerHistoryEntry[]> {
    await this.getClient(organizationId, relationshipId);
    return [...(state.history.get(relationshipId) ?? [])];
  }

  async enterClient(organizationId: string, relationshipId: string): Promise<EnterClientSummary> {
    requirePartner(organizationId);
    can('partner.clients.read');
    const denied = () => new ApiError(403, 'You cannot open this client.');
    if (state.status !== 'Active') throw denied();
    const found = (await this.listClients(organizationId)).find((c) => c.id === relationshipId);
    if (!found || (found.status !== 'Active' && found.status !== 'Onboarding')) throw denied();
    // the person must be assigned (every role, Owner and Admin included), and each service must be one their role may use
    const me = state.team.find((m) => m.role === state.callerRole)!;
    if (!(state.assignments.get(found.id) ?? []).some((a) => a.membershipId === me.membershipId)) throw denied();
    const usable = found.grantedServices.filter((service) => {
      if (found.status === 'Onboarding' && !MOCK_SETUP_SERVICES.has(service)) return false;
      const needed = MOCK_SERVICE_PERMISSION[service];
      return !!needed && ROLE_PERMISSIONS[state.callerRole].includes(needed);
    });
    const scopes = [...new Set(usable.flatMap((service) => MOCK_SERVICE_SCOPES[service] ?? []))].sort();
    if (scopes.length === 0) throw denied();
    const expiresAtUtc = new Date(Date.now() + 10 * 60_000).toISOString();
    setMockDelegation({
      info: { relationshipId: found.id, partnerOrganizationId: MOCK_PARTNER_ORG_ID, clientName: found.clientCompanyName ?? found.clientName, partnerFirmName: SYNTHETIC_FIRM_NAME, partnerRole: state.callerRole, scopes, expiresAtUtc, isStillValid: true },
      permissions: [...new Set(scopes.flatMap((scope) => MOCK_SCOPE_PERMISSIONS[scope] ?? []))].sort(),
    });
    record(found.id, 'PartnerClientEntered');
    return { relationshipId: found.id, clientName: found.clientCompanyName ?? found.clientName, partnerRole: state.callerRole, scopes, expiresAtUtc };
  }

  async exitClient(): Promise<void> {
    const current = getMockDelegation();
    if (!current) throw new ApiError(409, 'You are not working inside a client company.');
    record(current.info.relationshipId, 'PartnerClientExited');
    clearMockDelegation();
  }

  async listTeam(organizationId: string): Promise<PartnerTeamMember[]> {
    requirePartner(organizationId);
    can('partner.team.read');
    return state.team.map((m) => ({ ...m }));
  }

  async changeRole(organizationId: string, membershipId: string, role: PartnerRole): Promise<PartnerTeamMember> {
    requirePartner(organizationId);
    can('partner.team.manage');
    const target = state.team.find((m) => m.membershipId === membershipId && m.isActive);
    if (!target) throw new ApiError(404, 'Not found.');
    if (target.role === 'Owner' && role !== 'Owner' && state.team.filter((m) => m.isActive && m.role === 'Owner').length < 2) {
      throw new ApiError(409, 'A partner firm must keep at least one active Partner Owner. Make someone else an Owner first.');
    }
    if ((target.role === 'Owner' || role === 'Owner') && state.callerRole !== 'Owner') throw new ApiError(409, 'Only a Partner Owner can grant or change the Owner role.');
    target.role = role;
    return { ...target };
  }

  async deactivateMember(organizationId: string, membershipId: string): Promise<void> {
    requirePartner(organizationId);
    can('partner.team.manage');
    const target = state.team.find((m) => m.membershipId === membershipId && m.isActive);
    if (!target) throw new ApiError(404, 'Not found.');
    if (target.role === 'Owner' && state.team.filter((m) => m.isActive && m.role === 'Owner').length < 2) {
      throw new ApiError(409, 'A partner firm must keep at least one active Partner Owner. Make someone else an Owner first.');
    }
    target.isActive = false;
  }
}

const label = (code: string): ServiceOption => PARTNER_SERVICES.find((s) => s.code === code) ?? { code, label: code };

function asAuthorized(c: PartnerClient): AuthorizedPartner {
  return {
    id: c.id, partnerLegalName: SYNTHETIC_FIRM_NAME, partnerDba: null, status: c.status, grantedServices: c.grantedServices.map(label), requestedServices: c.requestedServices.map(label),
    accessExpiresAtUtc: c.accessExpiresAtUtc, acceptedAtUtc: c.acceptedAtUtc, activatedAtUtc: c.activatedAtUtc, suspendedAtUtc: null, suspensionReason: null, revokedAtUtc: c.revokedAtUtc,
    revocationReason: c.status === 'Revoked' ? 'Revoked by the company owner.' : null, lastReviewedAtUtc: c.lastReviewedAtUtc,
    assignedMembers: (state.assignments.get(c.id) ?? []).map((a) => ({ email: a.email, displayName: a.displayName, role: a.role })),
  };
}

/** The client owner's side of the same in-memory firm, so a demo can invite on one side and accept on the other. */
export class MockCompanyPartnersGateway implements CompanyPartnersGateway {
  /** A relationship is on this company's page when it is one of the demo company's or the owner accepted it in this session (a declined invitation never was). Read live, so a test reset takes effect. */
  private static isBound = (c: PartnerClient) => companyRelationshipIds.has(c.id);

  async list(): Promise<AuthorizedPartner[]> {
    return state.clients.filter(MockCompanyPartnersGateway.isBound).map(asAuthorized);
  }

  private find(token: string) {
    const id = state.tokens.get(token.trim());
    const found = id ? state.clients.find((c) => c.id === id) : undefined;
    if (!found || (found.status !== 'InvitationSent' && found.status !== 'AwaitingClientAcceptance')) throw new ApiError(404, 'Not found.');
    return found;
  }

  async previewInvitation(token: string): Promise<InvitationPreview> {
    const c = this.find(token);
    c.status = 'AwaitingClientAcceptance';
    return { partnerLegalName: SYNTHETIC_FIRM_NAME, partnerDba: null, clientName: c.clientName, requestedServices: c.requestedServices.map(label), invitationExpiresAtUtc: c.invitationExpiresAtUtc };
  }

  async acceptInvitation(token: string, services: string[]): Promise<AuthorizedPartner> {
    const c = this.find(token);
    if (services.length === 0 || services.some((s) => !c.requestedServices.includes(s))) throw new ApiError(400, 'You can only accept services the firm asked for.');
    c.status = 'Onboarding';
    c.grantedServices = [...services].sort();
    c.acceptedAtUtc = new Date().toISOString();
    c.clientCompanyName = c.clientName;
    state.tokens.delete(token.trim());
    companyRelationshipIds.add(c.id);
    record(c.id, 'PartnerClientRelationshipAccepted');
    return asAuthorized(c);
  }

  async declineInvitation(token: string): Promise<void> {
    const c = this.find(token);
    c.status = 'Revoked';
    c.revokedAtUtc = new Date().toISOString();
    state.tokens.delete(token.trim());
  }

  private owned(id: string) {
    const c = state.clients.find((x) => x.id === id && MockCompanyPartnersGateway.isBound(x));
    if (!c) throw new ApiError(404, 'Not found.');
    return c;
  }

  async reduceServices(id: string, services: string[]): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (services.length === 0 || services.some((s) => !c.grantedServices.includes(s))) throw new ApiError(400, 'Access can only be reduced here. To allow more, the firm must send a new invitation.');
    c.grantedServices = [...services].sort();
    record(c.id, 'PartnerClientServicesReduced');
    return asAuthorized(c);
  }

  async setExpiry(id: string, expiresAtUtc: string | null): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (expiresAtUtc && new Date(expiresAtUtc).getTime() <= Date.now()) throw new ApiError(400, 'The expiry date must be in the future.');
    c.accessExpiresAtUtc = expiresAtUtc;
    record(c.id, 'PartnerClientAccessExpiryChanged');
    return asAuthorized(c);
  }

  async activate(id: string): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (c.status !== 'Onboarding') throw new ApiError(409, 'That action is not available for this authorization in its current state.');
    c.status = 'Active';
    c.activatedAtUtc = new Date().toISOString();
    record(c.id, 'PartnerClientRelationshipActivated');
    return asAuthorized(c);
  }

  async suspend(id: string, reason: string): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (reason.trim().length < 10) throw new ApiError(400, 'A reason of at least 10 characters is required.');
    if (c.status !== 'Active' && c.status !== 'Onboarding') throw new ApiError(409, 'That action is not available for this authorization in its current state.');
    c.status = 'Suspended';
    invalidateMockDelegationFor(c.id);
    record(c.id, 'PartnerClientRelationshipSuspended', reason);
    return asAuthorized(c);
  }

  async resume(id: string): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (c.status !== 'Suspended') throw new ApiError(409, 'That action is not available for this authorization in its current state.');
    c.status = c.activatedAtUtc ? 'Active' : 'Onboarding';
    record(c.id, 'PartnerClientRelationshipResumed');
    return asAuthorized(c);
  }

  async revoke(id: string, reason: string): Promise<AuthorizedPartner> {
    const c = this.owned(id);
    if (reason.trim().length < 10) throw new ApiError(400, 'A reason of at least 10 characters is required.');
    if (c.status === 'Revoked' || c.status === 'Ended') throw new ApiError(409, 'That action is not available for this authorization in its current state.');
    c.status = 'Revoked';
    c.revokedAtUtc = new Date().toISOString();
    state.assignments.set(c.id, []);
    c.assignedMemberCount = 0;
    invalidateMockDelegationFor(c.id);
    record(c.id, 'PartnerClientRelationshipRevoked', reason);
    return asAuthorized(c);
  }

  async history(id: string): Promise<PartnerHistoryEntry[]> {
    this.owned(id);
    return [...(state.history.get(id) ?? [])];
  }
}
